import React from 'react';
import { Search, Puzzle, Rocket } from 'lucide-react';

const howItWorksData = {
  title: "From Idea to Production in Minutes",
  tagline: "No coding. No integration. No security headaches.",
  steps: [
    {
      number: "1",
      title: "Choose Your Domain",
      description: "Select from specialized agent packs",
      detail: "Pick from healthcare, logistics, e-commerce, and more. Each pack comes pre-loaded with industry-specific intelligence, compliance frameworks, and best practices.",
      icon: "Search"
    },
    {
      number: "2", 
      title: "Pick Components",
      description: "Customer, Inventory, Payments, etc.",
      detail: "Mix and match business components like building blocks. Our AI understands how they connect and automatically handles all integrations and data flows.",
      icon: "Puzzle"
    },
    {
      number: "3",
      title: "Deploy Instantly",
      description: "AI agents build and secure everything",
      detail: "Watch as AI agents generate, test, and deploy your complete business solution. Security, compliance, and optimizations are built in from day one.",
      icon: "Rocket"
    }
  ]
};

const iconMap = {
  Search: Search,
  Puzzle: Puzzle,
  Rocket: Rocket
};

const HowItWorks = () => {
  return (
    <section className="dark-full-container" style={{ paddingTop: '160px', paddingBottom: '160px' }}>
      <div className="dark-content-container">
        <div className="reveal" style={{ textAlign: 'center', marginBottom: '100px' }}>
          <h2 className="display-large" style={{ marginBottom: '24px', color: 'var(--brand-primary)' }}>
            {howItWorksData.title}
          </h2>
          <p className="body-large" style={{ maxWidth: '800px', margin: '0 auto 40px auto' }}>
            Transform your business ideas into production-ready applications with our simple 3-step process
          </p>
          <p className="heading-2" style={{ color: 'var(--brand-primary)', fontStyle: 'italic' }}>
            {howItWorksData.tagline}
          </p>
        </div>
        
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(350px, 1fr))',
          gap: '60px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {howItWorksData.steps.map((step, index) => {
            const IconComponent = iconMap[step.icon];
            return (
              <div 
                key={index}
                className="ui-card reveal"
                style={{
                  background: 'var(--bg-secondary)',
                  border: '1px solid var(--border-subtle)',
                  padding: '40px',
                  textAlign: 'center',
                  transitionDelay: `${index * 0.15}s`
                }}
              >
                <div className="icon-badge" style={{
                  width: '56px',
                  height: '56px',
                  borderRadius: '14px',
                  background: 'var(--brand-hover)',
                  color: 'var(--brand-primary)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  margin: '0 auto 20px'
                }}>
                  <IconComponent size={26} />
                </div>
                <div className="body-medium" style={{
                  color: 'var(--brand-primary)',
                  fontWeight: 700,
                  letterSpacing: '0.08em',
                  marginBottom: '8px'
                }}>
                  STEP {step.number}
                </div>
                <h3 className="heading-1" style={{ marginBottom: '12px' }}>
                  {step.title}
                </h3>
                <p className="body-medium" style={{ 
                  color: 'var(--brand-primary)', 
                  marginBottom: '20px',
                  fontWeight: 500,
                  fontSize: '18px'
                }}>
                  {step.description}
                </p>
                <p className="body-medium" style={{ 
                  lineHeight: 1.6,
                  color: 'var(--text-secondary)'
                }}>
                  {step.detail}
                </p>
              </div>
            );
          })}
        </div>
        
        {/* Connection Lines - Desktop Only */}
        <div className="reveal" style={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '80px',
          position: 'relative'
        }}>
          <div className="heading-2" style={{ 
            color: 'var(--brand-primary)',
            textAlign: 'center',
            maxWidth: '600px'
          }}>
            That's it. Your business ecosystem is live, secure, and ready to scale.
          </div>
        </div>
      </div>
    </section>
  );
};

export default HowItWorks;